import { onScopeDispose } from "vue";

export interface LatestRequest {
    /**
     * Start a new request. Aborts the previously-started one and returns this
     * request's monotonic token plus its own AbortSignal.
     */
    begin: () => { token: number; signal: AbortSignal };
    /**
     * True while `token` is still the most recently started request — i.e. no
     * newer begin() has happened since. Use it to guard every state commit so a
     * superseded request never clobbers fresher data.
     */
    isCurrent: (token: number) => boolean;
}

/**
 * "Latest request wins" guard for entity stores that reload on navigation.
 *
 * The old pattern — a shared `ref<AbortController>` that each fetch reassigns and
 * then re-reads as `controller.value.signal` *after* awaits — is racy: several
 * watchers fan into one fetch (identity, revision, the history sidebar's deep
 * watch), so a later run could abort an earlier run's in-flight sub-fetch, and a
 * stale run could overwrite fresher state. This gives each fetch invocation its
 * OWN controller (captured locally, never re-read reactively) and a token, so:
 *   - starting a new request aborts only the previous one;
 *   - a superseded request commits nothing (guard writes with `isCurrent`);
 *   - an aborted sub-fetch is a clean no-op rather than an unhandled rejection.
 */
export const useLatestRequest = (): LatestRequest => {
    let seq = 0;
    let controller: AbortController | undefined;

    const begin = () => {
        controller?.abort();
        controller = new AbortController();
        return { token: ++seq, signal: controller.signal };
    };

    const isCurrent = (token: number): boolean => token === seq;

    onScopeDispose(() => controller?.abort());

    return { begin, isCurrent };
};

/** True for the DOMException thrown when a fetch is aborted via AbortController. */
export const isAbortError = (e: unknown): boolean =>
    e instanceof DOMException && e.name === "AbortError";
